import React, { createContext, useContext, useState, ReactNode } from 'react';
import { api } from '../config/api';

interface City {
  id: number;
  name: string;
}

interface District {
  id: number;
  name: string;
  city_id: number;
}

interface Prison {
  id: number;
  name: string;
  address: string;
  city_id: number;
}

interface PrisonContextType {
  cities: City[];
  districts: District[];
  prisons: Prison[];
  setCities: (cities: City[]) => void;
  setDistricts: (districts: District[]) => void;
  setPrisons: (prisons: Prison[]) => void;
  fetchCities: () => Promise<void>;
  fetchDistricts: (cityId: number) => Promise<District[]>;
  fetchPrisons: (cityId: number) => Promise<Prison[]>;
}

const PrisonContext = createContext<PrisonContextType | undefined>(undefined);

export const usePrison = () => {
  const context = useContext(PrisonContext);
  if (!context) {
    throw new Error('usePrison must be used within a PrisonProvider');
  }
  return context;
};

interface PrisonProviderProps {
  children: ReactNode;
}

export const PrisonProvider: React.FC<PrisonProviderProps> = ({ children }) => {
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [prisons, setPrisons] = useState<Prison[]>([]);

  const fetchCities = async () => {
    // Eğer zaten şehirler yüklenmişse tekrar yükleme
    if (cities.length > 0) {
      return;
    }

    try {
      
      // Mobile'daki gibi axios instance kullan
      const response = await api.get('/locations/cities');
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        setCities(response.data);
      } else if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        setCities(response.data.data);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (error) {
      
      // Fallback: Statik şehir listesi
      const fallbackCities = [
        { id: 1, name: 'Adana' },
        { id: 2, name: 'Adıyaman' },
        { id: 3, name: 'Afyonkarahisar' },
        { id: 4, name: 'Ağrı' },
        { id: 5, name: 'Amasya' },
        { id: 6, name: 'Ankara' },
        { id: 7, name: 'Antalya' },
        { id: 8, name: 'Artvin' },
        { id: 9, name: 'Aydın' },
        { id: 10, name: 'Balıkesir' },
        { id: 11, name: 'Bilecik' },
        { id: 12, name: 'Bingöl' },
        { id: 13, name: 'Bitlis' },
        { id: 14, name: 'Bolu' },
        { id: 15, name: 'Burdur' },
        { id: 16, name: 'Bursa' },
        { id: 17, name: 'Çanakkale' },
        { id: 18, name: 'Çankırı' },
        { id: 19, name: 'Çorum' },
        { id: 20, name: 'Denizli' },
        { id: 21, name: 'Diyarbakır' },
        { id: 22, name: 'Edirne' },
        { id: 23, name: 'Elazığ' },
        { id: 24, name: 'Erzincan' },
        { id: 25, name: 'Erzurum' },
        { id: 26, name: 'Eskişehir' },
        { id: 27, name: 'Gaziantep' },
        { id: 28, name: 'Giresun' },
        { id: 29, name: 'Gümüşhane' },
        { id: 30, name: 'Hakkâri' },
        { id: 31, name: 'Hatay' },
        { id: 32, name: 'Isparta' },
        { id: 33, name: 'Mersin' },
        { id: 34, name: 'İstanbul' },
        { id: 35, name: 'İzmir' },
        { id: 36, name: 'Kars' },
        { id: 37, name: 'Kastamonu' },
        { id: 38, name: 'Kayseri' },
        { id: 39, name: 'Kırklareli' },
        { id: 40, name: 'Kırşehir' },
        { id: 41, name: 'Kocaeli' },
        { id: 42, name: 'Konya' },
        { id: 43, name: 'Kütahya' },
        { id: 44, name: 'Malatya' },
        { id: 45, name: 'Manisa' },
        { id: 46, name: 'Kahramanmaraş' },
        { id: 47, name: 'Mardin' },
        { id: 48, name: 'Muğla' },
        { id: 49, name: 'Muş' },
        { id: 50, name: 'Nevşehir' },
        { id: 51, name: 'Niğde' },
        { id: 52, name: 'Ordu' },
        { id: 53, name: 'Rize' },
        { id: 54, name: 'Sakarya' },
        { id: 55, name: 'Samsun' },
        { id: 56, name: 'Siirt' },
        { id: 57, name: 'Sinop' },
        { id: 58, name: 'Sivas' },
        { id: 59, name: 'Tekirdağ' },
        { id: 60, name: 'Tokat' },
        { id: 61, name: 'Trabzon' },
        { id: 62, name: 'Tunceli' },
        { id: 63, name: 'Şanlıurfa' },
        { id: 64, name: 'Uşak' },
        { id: 65, name: 'Van' },
        { id: 66, name: 'Yozgat' },
        { id: 67, name: 'Zonguldak' },
        { id: 68, name: 'Aksaray' },
        { id: 69, name: 'Bayburt' },
        { id: 70, name: 'Karaman' },
        { id: 71, name: 'Kırıkkale' },
        { id: 72, name: 'Batman' },
        { id: 73, name: 'Şırnak' },
        { id: 74, name: 'Bartın' },
        { id: 75, name: 'Ardahan' },
        { id: 76, name: 'Iğdır' },
        { id: 77, name: 'Yalova' },
        { id: 78, name: 'Karabük' },
        { id: 79, name: 'Kilis' },
        { id: 80, name: 'Osmaniye' },
        { id: 81, name: 'Düzce' },
      ];
      
      setCities(fallbackCities);
    }
  };

  const fetchDistricts = async (cityId: number): Promise<District[]> => {
    try {
      
      // Mobile'daki gibi axios instance kullan
      const response = await api.get(`/locations/cities/${cityId}/towns`);
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        setDistricts(response.data);
        return response.data;
      } else if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        setDistricts(response.data.data);
        return response.data.data;
      } else {
        throw new Error('Invalid data format');
      }
    } catch (error) {
      
      // Fallback: Statik ilçe listesi
      const cityName = cities.find(c => c.id === cityId)?.name || 'Şehir';
      const fallbackDistricts = [
        { id: 1, name: 'Merkez', city_id: cityId },
        { id: 2, name: `${cityName} 1. İlçe`, city_id: cityId },
        { id: 3, name: `${cityName} 2. İlçe`, city_id: cityId },
        { id: 4, name: `${cityName} 3. İlçe`, city_id: cityId },
      ];
      
      setDistricts(fallbackDistricts);
      return fallbackDistricts;
    }
  };

  const fetchPrisons = async (cityId: number): Promise<Prison[]> => {
    try {
      
      // Mobile'daki gibi axios instance kullan - cezaevleri için farklı endpoint olabilir
      const response = await api.get(`/locations/cities/${cityId}/prisons`);
      
      if (Array.isArray(response.data) && response.data.length > 0) {
        setPrisons(response.data);
        return response.data;
      } else if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        setPrisons(response.data.data);
        return response.data.data;
      } else {
        throw new Error('Invalid data format');
      }
    } catch (error) {
      
      // Fallback: Statik cezaevi listesi
      const cityName = cities.find(c => c.id === cityId)?.name || 'Şehir';
      const fallbackPrisons = [
        { 
          id: 1, 
          name: `${cityName} 1 Nolu L Tipi Kapalı Ceza İnfaz Kurumu`, 
          address: `${cityName} Merkez, Cezaevi Mahallesi No:1`, 
          city_id: cityId 
        },
        { 
          id: 2, 
          name: `${cityName} 2 Nolu T Tipi Kapalı Ceza İnfaz Kurumu`, 
          address: `${cityName} Merkez, Cezaevi Mahallesi No:2`, 
          city_id: cityId 
        },
        { 
          id: 3, 
          name: `${cityName} Açık Ceza İnfaz Kurumu`, 
          address: `${cityName} Merkez, Açık Cezaevi Mahallesi No:1`, 
          city_id: cityId 
        },
      ];
      
      setPrisons(fallbackPrisons);
      return fallbackPrisons;
    }
  };

  const value: PrisonContextType = {
    cities,
    districts,
    prisons,
    setCities,
    setDistricts,
    setPrisons,
    fetchCities,
    fetchDistricts,
    fetchPrisons,
  };

  return (
    <PrisonContext.Provider value={value}>
      {children}
    </PrisonContext.Provider>
  );
};

export { PrisonContext };
